import React, { useState, useRef, useEffect } from 'react'
import PropTypes from 'prop-types'
import { Button, Slider, Radio, Tooltip, message, Space } from 'antd'
import {
  ZoomInOutlined,
  ZoomOutOutlined,
  UndoOutlined,
  RedoOutlined
} from '@ant-design/icons'
import { postRequest, successNotification, carsUrl } from '../../config'
import { pixelateRegion } from './pixelate'

const EditImage = props => {
  const { currentImage, handleSuccess } = props
  const [loading, setLoading] = useState(false)

  const canvasRef = useRef(null)
  const viewportRef = useRef(null)
  // The canvas as it was when this drag started, so a blur selection can be previewed by redrawing
  // it under the selection rectangle. Deliberately a ref, not state: it changes mid-gesture and
  // must not re-render anything.
  const dragSnapshotRef = useRef(null)
  const [isDrawing, setIsDrawing] = useState(false)
  const [tool, setTool] = useState('blur') // 'blur' or 'brush'
  const [brushSize, setBrushSize] = useState(20)
  const [brushColor, setBrushColor] = useState('#000000')
  const [startPos, setStartPos] = useState({ x: 0, y: 0 })
  const [history, setHistory] = useState([]) // To store canvas states for undo
  const [redoHistory, setRedoHistory] = useState([]) // To store canvas states for redo
  const [scale, setScale] = useState(1)
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 })

  // Load image onto canvas
  useEffect(() => {
    if (currentImage && currentImage.image) {
      const img = new Image()
      img.crossOrigin = 'anonymous'
      img.src = currentImage.image
      img.onload = () => {
        const canvas = canvasRef.current
        if (canvas) {
          // Set canvas to match image dimensions
          canvas.width = img.width
          canvas.height = img.height
          const ctx = canvas.getContext('2d')
          ctx.drawImage(img, 0, 0)

          // Save initial state
          setHistory([canvas.toDataURL()])
          setRedoHistory([])
          setImgSize({ width: img.width, height: img.height })
          // Start at a scale that fits, rather than at 100% of a 4000px photo. At 100% a phone
          // was panning the full natural width through a ~350px window, and zoom only went down
          // to 0.5 — so most of the picture could not be reached at all.
          setScale(fitScale(img.width))
        }
      }
      img.onerror = () => {
        message.error('Failed to load image for editing')
      }
    }
  }, [currentImage])

  // Save state to history for Undo
  const saveState = () => {
    const canvas = canvasRef.current
    if (canvas) {
      const newState = canvas.toDataURL()
      setHistory(prev => [...prev, newState])
      setRedoHistory([]) // Clear redo history on new action
    }
  }

  const undo = () => {
    if (history.length > 1) {
      const newHistory = [...history]
      const currentState = newHistory.pop() // Remove current state
      setRedoHistory(prev => [...prev, currentState]) // Add to redo stack
      const prevState = newHistory[newHistory.length - 1]
      setHistory(newHistory)

      const img = new Image()
      img.src = prevState
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d')
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
        ctx.drawImage(img, 0, 0)
      }
    }
  }

  const redo = () => {
    if (redoHistory.length > 0) {
      const newRedoHistory = [...redoHistory]
      const nextState = newRedoHistory.pop() // Get state to redo
      setRedoHistory(newRedoHistory)
      setHistory(prev => [...prev, nextState]) // Add back to history

      const img = new Image()
      img.src = nextState
      img.onload = () => {
        const ctx = canvasRef.current.getContext('2d')
        ctx.clearRect(0, 0, canvasRef.current.width, canvasRef.current.height)
        ctx.drawImage(img, 0, 0)
      }
    }
  }

  // The width the image can have without the viewport needing to scroll. Below 1 on a phone for
  // any real photo; capped at 1 so a small image is not blown up.
  const fitScale = naturalWidth => {
    const available = viewportRef.current
      ? viewportRef.current.clientWidth - 20 // the viewport's own padding
      : 0
    if (!available || !naturalWidth) return 1
    return Math.min(1, available / naturalWidth)
  }

  const handleZoomIn = () => {
    setScale(prev => Math.min(prev + 0.1, 3))
  }

  const handleZoomOut = () => {
    // Floor of 0.1 rather than 0.5: a 4000px photo at 0.5 is still 2000px wide.
    setScale(prev => Math.max(prev - 0.1, 0.1))
  }

  const handleFit = () => setScale(fitScale(imgSize.width))

  // Canvas coordinates from a pointer event. Pointer events carry clientX/clientY for a finger,
  // a stylus and a mouse alike, which is what makes one code path enough.
  const getPointerPos = e => {
    const canvas = canvasRef.current
    const rect = canvas.getBoundingClientRect()
    // The canvas is displayed at a scaled size, so a CSS pixel is not an image pixel.
    const scaleX = canvas.width / rect.width
    const scaleY = canvas.height / rect.height
    return {
      x: (e.clientX - rect.left) * scaleX,
      y: (e.clientY - rect.top) * scaleY
    }
  }

  const startDrawing = e => {
    const canvas = canvasRef.current
    if (!canvas) return
    // Keep every later event in this gesture coming to the canvas even if the finger slides off it,
    // so a drag that ends outside still applies instead of being abandoned half-done.
    if (e.pointerId !== undefined && canvas.setPointerCapture) {
      try {
        canvas.setPointerCapture(e.pointerId)
      } catch (err) {
        /* some browsers refuse this for a mouse; the gesture still works */
      }
    }
    const pos = getPointerPos(e)
    setStartPos(pos)
    setIsDrawing(true)

    const ctx = canvas.getContext('2d')
    if (tool === 'blur') {
      // Remember the clean canvas so the selection rectangle can be drawn and erased on top of it.
      dragSnapshotRef.current = ctx.getImageData(0, 0, canvas.width, canvas.height)
    }
    ctx.beginPath()
    ctx.moveTo(pos.x, pos.y)

    if (tool === 'brush') {
      ctx.lineWidth = brushSize
      ctx.lineCap = 'round'
      ctx.strokeStyle = brushColor
    }
  }

  const draw = e => {
    if (!isDrawing) return
    const pos = getPointerPos(e)
    const canvas = canvasRef.current
    const ctx = canvas.getContext('2d')

    if (tool === 'brush') {
      ctx.lineTo(pos.x, pos.y)
      ctx.stroke()
    } else if (tool === 'blur' && dragSnapshotRef.current) {
      // Show the area being selected. Without this you dragged blind and only saw the result on
      // release — which on a phone, where you cannot see under your own finger, meant guessing.
      ctx.putImageData(dragSnapshotRef.current, 0, 0)
      ctx.save()
      ctx.strokeStyle = '#1890ff'
      ctx.lineWidth = Math.max(2, 2 / Math.max(scale, 0.1))
      ctx.setLineDash([8, 6])
      ctx.strokeRect(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y)
      ctx.restore()
    }
  }

  const stopDrawing = e => {
    if (!isDrawing) return
    setIsDrawing(false)
    const pos = getPointerPos(e)

    if (tool === 'blur') {
      // Put the clean pixels back before blurring, or the selection rectangle gets pixelated in.
      if (dragSnapshotRef.current) {
        canvasRef.current.getContext('2d').putImageData(dragSnapshotRef.current, 0, 0)
        dragSnapshotRef.current = null
      }
      applyBlur(startPos.x, startPos.y, pos.x - startPos.x, pos.y - startPos.y)
    }

    saveState()
  }

  const cancelDrawing = () => {
    if (!isDrawing) return
    setIsDrawing(false)
    if (tool === 'blur' && dragSnapshotRef.current) {
      canvasRef.current.getContext('2d').putImageData(dragSnapshotRef.current, 0, 0)
      dragSnapshotRef.current = null
    }
  }

  const applyBlur = (x, y, w, h) => {
    const ctx = canvasRef.current.getContext('2d')

    // Normalize negative width/height
    if (w < 0) {
      x += w
      w = -w
    }
    if (h < 0) {
      y += h
      h = -h
    }

    // Clamp to the canvas before reading anything back.
    const canvas = canvasRef.current
    x = Math.max(0, Math.round(x))
    y = Math.max(0, Math.round(y))
    w = Math.round(Math.min(w, canvas.width - x))
    h = Math.round(Math.min(h, canvas.height - y))
    if (w < 1 || h < 1) return

    // One read, one write. It used to call getImageData(i, j, 1, 1) per 10px block — thousands of
    // separate readbacks for a full-size photo, which is what made this crawl on a phone.
    let region
    try {
      region = ctx.getImageData(x, y, w, h)
    } catch (err) {
      // A canvas tainted by a cross-origin image cannot be read back at all.
      message.error('This image cannot be edited here (its host does not allow it)')
      return
    }
    pixelateRegion(region.data, w, h, 10)
    ctx.putImageData(region, x, y)
  }

  const handleSubmit = async () => {
    setLoading(true)
    const canvas = canvasRef.current

    canvas.toBlob(
      async blob => {
        if (!blob) {
          setLoading(false)
          message.error('Failed to process image')
          return
        }

        const formData = new FormData()
        formData.append('image', blob, 'edited_image.jpg')
        formData.append('image_id', currentImage.id)
        formData.append('is_showcase_image', currentImage.isShowcaseImage)
        formData.append('active', currentImage.active)
        formData.append('is_video', currentImage.isVideo)
        formData.append('video_id', currentImage.videoId || '')

        const response = await postRequest(
          `${carsUrl}/update-image`,
          formData,
          'image',
          setLoading,
          false
        )

        if (response.success) {
          handleSuccess(response.data)
          successNotification('Image Updated Successfully')
        } else {
          if (response.error.response) {
            message.error('Failed to update image')
          }
        }
        setLoading(false)
      },
      'image/jpeg',
      0.9
    )
  }

  return (
    <div className="image-editor" style={{ padding: 20 }}>
      {/* Tools Toolbar */}
      <div
        style={{
          marginBottom: 15,
          borderBottom: '1px solid #eee',
          paddingBottom: 10
        }}
      >
        <div
          style={{
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            flexWrap: 'wrap'
          }}
        >
          <div style={{ display: 'flex', alignItems: 'center', gap: '15px' }}>
            <Radio.Group
              value={tool}
              onChange={e => setTool(e.target.value)}
              buttonStyle="solid"
            >
              <Radio.Button value="blur">Blur (Pixelate)</Radio.Button>
              <Radio.Button value="brush">Brush</Radio.Button>
            </Radio.Group>

            {tool === 'brush' && (
              <div
                style={{ display: 'flex', alignItems: 'center', gap: '10px' }}
              >
                <span>Size:</span>
                <Slider
                  min={5}
                  max={50}
                  value={brushSize}
                  onChange={setBrushSize}
                  style={{ width: 100 }}
                />
                <span>Color:</span>
                <input
                  type="color"
                  value={brushColor}
                  onChange={e => setBrushColor(e.target.value)}
                  style={{
                    height: 32,
                    width: 50,
                    padding: 0,
                    border: 'none',
                    cursor: 'pointer'
                  }}
                />
              </div>
            )}
          </div>

          <Space>
            <div
              style={{
                display: 'flex',
                alignItems: 'center',
                gap: '5px',
                borderRight: '1px solid #ddd',
                paddingRight: '10px',
                marginRight: '5px'
              }}
            >
              <Button
                icon={<ZoomOutOutlined />}
                onClick={handleZoomOut}
                disabled={scale <= 0.1}
              />
              <span style={{ minWidth: '40px', textAlign: 'center' }}>
                {Math.round(scale * 100)}%
              </span>
              <Button
                icon={<ZoomInOutlined />}
                onClick={handleZoomIn}
                disabled={scale >= 3}
              />
              <Button onClick={handleFit}>Fit</Button>
            </div>

            <Tooltip title="Undo (Ctrl+Z)">
              <Button
                icon={<UndoOutlined />}
                onClick={undo}
                disabled={history.length <= 1}
              >
                Undo
              </Button>
            </Tooltip>
            <Tooltip title="Redo (Ctrl+Y)">
              <Button
                icon={<RedoOutlined />}
                onClick={redo}
                disabled={redoHistory.length === 0}
              >
                Redo
              </Button>
            </Tooltip>
          </Space>
        </div>

        <div style={{ marginTop: 8, fontSize: '0.9em', color: '#666' }}>
          {tool === 'blur'
            ? 'Drag across the area to pixelate — the dotted box shows what will be covered.'
            : 'Drag to paint over the image.'}
        </div>
      </div>

      {/* Editor Area */}
      <div
        ref={viewportRef}
        style={{
          overflow: 'auto',
          maxHeight: '60vh',
          border: '1px solid #ccc',
          backgroundColor: '#f0f0f0',
          display: 'flex',
          padding: '10px'
        }}
      >
        {/* Pointer events, not mouse events. The handlers here were onMouseDown/Move/Up only, and
            getMousePos read e.clientX — which a TouchEvent does not have — so on a phone blur and
            brush did nothing at all, not merely something awkward. One pointer path serves a
            finger, a stylus and a mouse. touchAction: none stops the browser treating the drag as
            a scroll or a pinch before the canvas ever sees it. */}
        <canvas
          ref={canvasRef}
          onPointerDown={startDrawing}
          onPointerMove={draw}
          onPointerUp={stopDrawing}
          onPointerCancel={cancelDrawing}
          style={{
            maxWidth: 'none', // Allow it to overflow for zoom
            width: imgSize.width * scale,
            height: imgSize.height * scale,
            margin: 'auto',
            cursor: tool === 'blur' ? 'crosshair' : 'default',
            display: 'block',
            touchAction: 'none',
            boxShadow: '0 0 10px rgba(0,0,0,0.1)'
          }}
        />
      </div>

      {/* Actions */}
      <div style={{ marginTop: 20, textAlign: 'right' }}>
        <Button
          type="primary"
          onClick={handleSubmit}
          loading={loading}
          size="large"
        >
          Save Changes
        </Button>
      </div>
    </div>
  )
}

EditImage.propTypes = {
  currentImage: PropTypes.object.isRequired,
  handleSuccess: PropTypes.func.isRequired
}

export default EditImage
